// RevenueSharingTable.js
import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatCurrency, parseLocalizedNumber } from "../helpers";

export function RevenueSharingTable({
  rows = [],
  units = [],
  ChangeText,
  selectedChange,
}) {
  const data = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  if (data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No revenue sharing data.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        overflowX: "auto",
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Table size="small" aria-label="Revenue sharing table">
        <TableHead>
          <TableRow sx={{ bgcolor: (t) => t.palette.action.hover }}>
            <TableCell>Unit</TableCell>
            <TableCell align="right">Percent</TableCell>
            <TableCell align="right">Eur</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <TableRow
              key={idx}
              hover
              sx={{
                "&:nth-of-type(even)": {
                  bgcolor: (t) => t.palette.action.focus,
                },
              }}
            >
              <TableCell>
                {selectedChange ? (
                  <ChangeText
                    newValue={
                      units.find((u) => row.unit.new === u.unit_id)
                        ?.unit_name ?? null
                    }
                    oldValue={
                      units.find((u) => row.unit.old === u.unit_id)
                        ?.unit_name ?? null
                    }
                  ></ChangeText>
                ) : (
                  units.find((u) => row.unit === u.unit_id)?.unit_name ?? "—"
                )}
              </TableCell>
              <TableCell align="right">
                {selectedChange ? (
                  <ChangeText
                    newValue={formatCurrency(row.percent.new)}
                    oldValue={formatCurrency(row.percent.old)}
                  ></ChangeText>
                ) : (
                  formatCurrency(row.percent)
                )}
              </TableCell>
              <TableCell align="right">
                {selectedChange ? (
                  <ChangeText
                    newValue={formatCurrency(row.euro.new)}
                    oldValue={formatCurrency(row.euro.old)}
                  ></ChangeText>
                ) : (
                  formatCurrency(row.euro)
                )}
              </TableCell>
            </TableRow>
          ))}
          <TableRow
            key="total"
            hover
            sx={{ bgcolor: (t) => t.palette.action.hover }}
          >
            <TableCell sx={{ whiteSpace: "wrap" }}>Total</TableCell>
            <TableCell align="right">
              {selectedChange ? (
                <ChangeText
                  newValue={formatCurrency(
                    data.reduce(
                      (sum, item) =>
                        sum + (Number.parseFloat(item.percent.new) || 0),
                      0
                    )
                  )}
                  oldValue={formatCurrency(
                    data.reduce(
                      (sum, item) =>
                        sum + (Number.parseFloat(item.percent.old) || 0),
                      0
                    )
                  )}
                ></ChangeText>
              ) : (
                formatCurrency(
                  data.reduce(
                    (sum, item) => sum + (Number.parseFloat(item.percent) || 0),
                    0
                  )
                )
              )}
            </TableCell>
            <TableCell align="right">
              {selectedChange ? (
                <ChangeText
                  newValue={formatCurrency(
                    data.reduce(
                      (sum, item) =>
                        sum + (parseLocalizedNumber(item.euro.new) || 0),
                      0
                    )
                  )}
                  oldValue={formatCurrency(
                    data.reduce(
                      (sum, item) =>
                        sum + (parseLocalizedNumber(item.euro.old) || 0),
                      0
                    )
                  )}
                ></ChangeText>
              ) : (
                formatCurrency(
                  data.reduce(
                    (sum, item) => sum + (parseLocalizedNumber(item.euro) || 0),
                    0
                  )
                )
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
